/**
 * RichText — rendu sûr du sous-ensemble markdown de Break News.
 *
 * Sécurité (même doctrine que Linkify, qu'il remplace) :
 *   - JAMAIS de dangerouslySetInnerHTML : on construit du JSX, pas du HTML
 *   - rel="noopener noreferrer" + target="_blank" sur chaque lien
 *   - aucune balise HTML de l'utilisateur n'est interprétée : `<b>bonjour</b>`
 *     s'affiche littéralement, il n'y a pas de surface XSS
 *
 * Supporté : **gras**, *italique*, ~~barré~~, `- ` et `1. ` (listes),
 * `# ` à `###### ` (titres), URLs auto-liées, emoji natifs.
 *
 * NOTE iOS : aucun lookbehind dans les regex (cf. lib/markdown.js).
 */
import React from "react";

// L'URL est en PREMIER dans l'alternance : une URL contenant des `*`
// ne doit pas être coupée par la règle italique.
const INLINE_RX =
  /(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]}]|\*\*[^*]+\*\*|~~[^~]+~~|\*[^*\n]+\*)/g;

const UL_RX = /^\s{0,3}[-*]\s+(.*)$/;
const OL_RX = /^\s{0,3}(\d+)\.\s+(.*)$/;
const H_RX = /^\s{0,3}(#{1,6})\s+(.*)$/;

function renderInline(text, keyPrefix) {
  const parts = String(text ?? "").split(INLINE_RX);
  return parts.filter(Boolean).map((part, i) => {
    const key = `${keyPrefix}-${i}`;

    if (/^https?:\/\//i.test(part)) {
      return (
        <a
          key={key}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-700 underline underline-offset-2 hover:text-emerald-800 break-all"
          data-testid="post-link"
        >
          {part}
        </a>
      );
    }
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={key} className="font-bold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith("~~") && part.endsWith("~~")) {
      return (
        <s key={key} style={{ opacity: 0.7 }}>
          {part.slice(2, -2)}
        </s>
      );
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return (
        <em key={key} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

/**
 * Regroupe les lignes en blocs (paragraphes, listes, titres) pour éviter
 * qu'une liste ne se rende comme une suite de lignes isolées.
 */
function toBlocks(lines) {
  return lines.reduce((blocks, line, idx) => {
    const last = blocks[blocks.length - 1];

    const ul = line.match(UL_RX);
    if (ul) {
      if (last?.type === "ul") last.items.push({ idx, text: ul[1] });
      else blocks.push({ type: "ul", items: [{ idx, text: ul[1] }] });
      return blocks;
    }

    const ol = line.match(OL_RX);
    if (ol) {
      if (last?.type === "ol") last.items.push({ idx, text: ol[2] });
      else blocks.push({ type: "ol", items: [{ idx, text: ol[2] }] });
      return blocks;
    }

    const h = line.match(H_RX);
    if (h) {
      blocks.push({ type: "h", level: h[1].length, idx, text: h[2] });
      return blocks;
    }

    if (!line.trim()) {
      blocks.push({ type: "spacer", idx });
      return blocks;
    }

    if (last?.type === "p") last.lines.push({ idx, text: line });
    else blocks.push({ type: "p", lines: [{ idx, text: line }] });
    return blocks;
  }, []);
}

export function RichText({ children, className }) {
  const text = typeof children === "string" ? children : String(children ?? "");
  if (!text) return null;

  const blocks = toBlocks(text.split("\n"));

  return (
    <span className={className} data-testid="richtext">
      {blocks.map((b) => {
        if (b.type === "spacer") {
          return <span key={`sp-${b.idx}`} className="block h-2" aria-hidden="true" />;
        }
        if (b.type === "h") {
          const size = b.level <= 2 ? "text-lg" : "text-base";
          return (
            <span
              key={`h-${b.idx}`}
              className={`block font-bold mt-3 mb-1 ${size}`}
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {renderInline(b.text, `h${b.idx}`)}
            </span>
          );
        }
        if (b.type === "ul" || b.type === "ol") {
          const Tag = b.type === "ul" ? "ul" : "ol";
          return (
            <Tag
              key={`${b.type}-${b.items[0].idx}`}
              className={`${
                b.type === "ul" ? "list-disc" : "list-decimal"
              } list-outside pl-5 my-2 space-y-0.5`}
            >
              {b.items.map((it) => (
                <li key={`li-${it.idx}`}>{renderInline(it.text, `li${it.idx}`)}</li>
              ))}
            </Tag>
          );
        }
        // paragraphe : les retours à la ligne simples sont préservés
        return (
          <span key={`p-${b.lines[0].idx}`} className="block">
            {b.lines.map((l, i) => (
              <React.Fragment key={`pl-${l.idx}`}>
                {i > 0 && <br />}
                {renderInline(l.text, `p${l.idx}`)}
              </React.Fragment>
            ))}
          </span>
        );
      })}
    </span>
  );
}

export default RichText;
